import { useEffect, useState } from 'react';
import { Container, Section, showAlert, showConfirm } from '@platform/ui';
import { toUserMessage } from '@platform/api-client';
import { SEO } from '@platform/seo';
import { buildSeo } from '@/shared/seo/seoBuilder';
import { useBoardList } from '../api/use-board.query.ts';
import { useDeletePost } from '../api/use-board.mutation.ts';
import { useBoardQueryState } from '../hooks/useBoardQueryState.ts';
import { useBoardPermissions } from '../hooks/useBoardPermissions.ts';
import { useDebouncedValue } from '../hooks/useDebouncedValue.ts';
import { BoardHero } from '../common/BoardHero.tsx';
import { BoardPagination } from '../common/BoardPagination.tsx';
import { BoardWriteButton } from '../common/BoardWriteButton.tsx';
import { BoardStateView } from '../components/BoardStateView.tsx';
import { BoardEmptyState } from '../components/BoardEmptyState.tsx';
import { FaqAccordionItem } from '../components/FaqAccordionItem.tsx';
import { BoardCategoryTabs } from '../components/BoardCategoryTabs.tsx';
import { BoardSubCategoryFilter } from '../components/BoardSubCategoryFilter.tsx';
import {
  getFaqSubCategoryOptions,
  isFaqCategory,
  isFaqSubCategory,
} from '../constants/faq.ts';
import { DEFAULT_PAGE } from '../constants/permissions.ts';
import type { BoardConfig, FaqPost, Post } from '../types/board.type.ts';

interface FaqAccordionViewProps {
  config: BoardConfig<Post>;
}


/**
 * FAQ 공개 화면 — 게시판 인프라(useBoardList)를 그대로 쓰되 아코디언으로 렌더한다.
 *
 * - size 를 크게 잡고 includeBody=true 로 요청해 답변까지 한 번에 받는다(FAQ 는 총량이 작음).
 * - 분류 탭/검색은 다른 게시판과 동일하게 서버에 위임(useBoardQueryState).
 * - 운영자(MANAGER+)에게만 글쓰기 버튼 노출(서버에서 최종 인가).
 */
export function FaqAccordionView({ config }: FaqAccordionViewProps) {
  const { params, setPage, setSearch, setCategory, setSubCategory } =
    useBoardQueryState();
  const perms = useBoardPermissions('faqs');
  const deleteMutation = useDeletePost('faqs');

  // FAQ 는 상세 화면이 없어서 삭제를 아코디언 안에서 처리한다. 확인·실패 안내는 상세 화면과 같다.
  const handleDelete = async (faq: FaqPost) => {
    const ok = await showConfirm({
      title: 'FAQ 삭제',
      message: '이 FAQ를 삭제하시겠습니까? 되돌릴 수 없습니다.',
      tone: 'danger',
      confirmText: '삭제',
      cancelText: '취소',
      dismissible: false,
    });
    if (!ok) return;

    deleteMutation.mutate(faq.id, {
      onSuccess: () => setOpenFaqId(null),
      onError: (error) =>
        void showAlert({
          title: '삭제 실패',
          message: toUserMessage(error, '삭제 중 오류가 발생했습니다.'),
          tone: 'danger',
          confirmText: '확인',
        }),
    });
  };

  const [searchInput, setSearchInput] = useState(params.search ?? '');
  const [openFaqId, setOpenFaqId] = useState<FaqPost['id'] | null>(null);
  const debouncedSearch = useDebouncedValue(searchInput, 350);
  const selectedCategory = isFaqCategory(params.category)
    ? params.category
    : null;
  const subCategoryOptions = getFaqSubCategoryOptions(selectedCategory);

  useEffect(() => {
    if (debouncedSearch !== (params.search ?? '')) {
      setSearch(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    if (
      params.subCategory &&
      !isFaqSubCategory(selectedCategory, params.subCategory)
    ) {
      setSubCategory(null);
    }
  }, [params.subCategory, selectedCategory, setSubCategory]);

  const { data, isLoading, isError, isFetching, refetch } =
    useBoardList<FaqPost>('faqs', {
      ...params,
      size: 30,
      includeBody: true,
    });

  const items = data?.items ?? [];
  const totalPages = Math.max(
    DEFAULT_PAGE,
    Math.ceil((data?.total ?? 0) / 30),
  );

  const seo = buildSeo({
    title: config.title,
    description: config.description,
    path: config.basePath,
  });

  return (
    <>
      <SEO {...seo} />
      <main className="overflow-x-hidden bg-white dark:bg-dark-base text-neutral-950 dark:text-neutral-100 antialiased">
        <BoardHero
          config={config}
          totalCount={data?.total ?? 0}
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          divider={false}
          action={
            perms.canCreate ? (
              <BoardWriteButton to={config.writePath()}>FAQ 작성</BoardWriteButton>
            ) : null
          }
        />

        <Section spacing="none" className="pb-20 sm:pb-[120px]">
          <Container>
            <div className="mb-4 min-w-0">
              <BoardCategoryTabs
                options={config.categories ?? []}
                value={selectedCategory}
                onChange={setCategory}
                ariaLabel="FAQ 대분류"
              />
              {selectedCategory && (
                <BoardSubCategoryFilter
                  options={subCategoryOptions}
                  value={params.subCategory ?? null}
                  onChange={setSubCategory}
                  ariaLabel="FAQ 중분류"
                />
              )}
            </div>

            {isLoading ? (
              <BoardStateView kind="loading" />
            ) : isError ? (
              <BoardStateView kind="error" onRetry={() => refetch()} />
            ) : items.length === 0 ? (
              <BoardEmptyState
                title="등록된 FAQ가 없습니다."
                description="다른 검색어나 분류로 다시 시도해 보세요."
              />
            ) : (
              <ul
                className={`transition-opacity ${
                  isFetching ? 'opacity-60' : ''
                }`}
              >
                {items.map((faq) => (
                  <FaqAccordionItem
                    key={String(faq.id)}
                    faq={faq}
                    isOpen={openFaqId === faq.id}
                    onToggle={() =>
                      setOpenFaqId((currentId) =>
                        currentId === faq.id ? null : faq.id,
                      )
                    }
                    editPath={
                      perms.canUpdate(faq) ? config.editPath(faq.id) : null
                    }
                    onDelete={
                      perms.canDelete(faq) ? () => void handleDelete(faq) : null
                    }
                    deleting={
                      deleteMutation.isPending &&
                      deleteMutation.variables === faq.id
                    }
                  />
                ))}
              </ul>
            )}

            <BoardPagination
              currentPage={params.page}
              totalPages={totalPages}
              onChange={setPage}
            />
          </Container>
        </Section>
      </main>
    </>
  );
}
